/**
 * Domain registry (UI-rewrite, Three-Layer PRD §5.1 — the NEW Layer-2 primitive).
 *
 * The modes flip shipped Layer 1 (Modes) + Layer 3 (Objects) and DELETED the
 * middle: Layer 2, the "apps" (CRM, ATS, Lens, Docs, Finance, Pipeline) that the
 * sections/products already are. The redirect orphaned them (64 LOST / 27
 * DEGRADED, ~85% Orphaned-not-gone). This module restores Layer 2 as data: a
 * Domain is "these object kinds, these views, this home" under a mode, and the
 * per-mode sub-nav is GENERATED from it (a new app = a new entry, never a new
 * mode — the five-mode invariant holds).
 *
 * **Data-only / serialisable (LOCKED #4).** `Domain`/`DomainView` carry NO React
 * and no imports beyond the `ModeSlug` type — `icon` and `component` are string
 * keys, resolved to JSX/components in the app-tree mount map
 * (`excellent/app/_modes/domain-mounts.tsx`). Same discipline as
 * `jtbd-registry.ts`/`section-registry.ts`, so this registry is mirror-ready to
 * the MCP package the moment a tool needs it (§5.6 — not in the first pass).
 *
 * **Domain-key convention (NORMATIVE §5.1).** A Domain's `slug` (its `?area=`
 * value) is the existing section/product slug (`sales`, `hiring`, `finance`,
 * `docs`, `lens`, `reviews`) — exactly what `redirects.ts` stamps as `extra.area`.
 * The per-app surfaces (Pipeline/Contacts/Jobs/Scorecards) are `DomainView`s
 * addressed by `?sub=<viewSlug>`, never `?area=<viewSlug>`. Work is the one
 * principled exception: it slices by OWNER, not a section, so each Work domain is
 * its own `slug` (queue/reviews/goals/planner/inbox), keyed directly in
 * `CORE_ROUTE`.
 *
 * PURE substrate: no flag read, no I/O. `ModePage` resolves `?area`→Domain and
 * `?sub`→view (§5.3); the generic 4-view `ModeShell` is the FALLBACK for an
 * unbuilt domain, never the whole mode. Built unconditionally (the
 * NEXT_PUBLIC_MODES_NAV flag is retired).
 *
 * **File-size waiver (thermo-nuclear standard 2, task #43607, 2026-07-23).**
 * This file crossed the ~1,000-line boundary (977→1107) landing the
 * Company/Team/Hiring fold (0fa2a4e3). Waived, not split: `DOMAINS` is one
 * flat, declarative array, and each entry's inline comment is load-bearing
 * decision history (a re-fold, a rename, a demote-don't-delete alias) that an
 * actively-evolving nav IA needs sitting beside the entry it explains, not in
 * a companion doc that drifts the next time an entry moves. Consistent with
 * the two sibling registries this file's own discipline model already names
 * above (jtbd-registry.ts, 1,626 lines; section-registry.ts, 1,447 lines) —
 * neither is split by size. Measured: ~442 of 1,107 lines (40%) are comments;
 * the array stripped of them would still be a single ~600-line cohesive unit,
 * so moving it to its own file would relocate the bulk across an import, not
 * reduce it.
 */

import { modeForKind, type ModeSlug } from "./modes-registry";

/** Tier mirrors artifact 14 — drives nav prominence + the re-flip coverage gate. */
export type DomainTier = "T1" | "T2" | "T3";

/** How a view renders — maps 1:1 onto an @excellent/ui shell or the orphaned page
 *  it mounts (§5.5 view contract). Informational; the mount map binds the actual
 *  component. */
export type DomainViewKind =
  | "board" // KindBoard / a domain's own kanban (PipelineKanban)
  | "list" // DataTable / SortableTable over the kind
  | "dashboard" // StatCard grid + attention (the domain's overview)
  | "detail" // a single-object surface
  | "custom"; // the domain mounts its own rich component (Lens, sequences…)

export interface DomainView {
  /** `?sub=` value that selects this view (slug-unique within the domain). */
  slug: string;
  /** Sub-nav label (Title-case; PageNav renders it). */
  label: string;
  kind: DomainViewKind;
  /**
   * EXACTLY ONE of `component` | `route` is set (enforced by check-domain-mounts).
   *  • `component` — a string KEY into the per-mode mount map (§5.4). The registry
   *    is data-only, so it never imports a React component; the key resolves to
   *    the orphaned page in the app-tree mount map.
   *  • `route` — an absolute path this view IS (used when the domain owns a full
   *    route subtree, e.g. Lens at `/agents/lens`). ModePage emits a `redirect()`
   *    to it rather than rendering inline. Only routes EXEMPT from the modes
   *    redirect (`/agents/*`, NON_MODE roots, public careers) are loop-safe here.
   */
  component?: string;
  route?: string;
  /** Optional default sub-area / query the mounted page reads. */
  defaultQuery?: Record<string, string>;
  /**
   * The section-hosted tree this view's `basePath` points at when it differs
   * from the owning domain's slug — a FOLDED domain mounting another section's
   * views (Company mounting the hiring ATS: its links are all
   * `${basePath}/jobs/…`, which must stay `/projects/sections/hiring/*`).
   * Absent → the domain slug (the overwhelmingly common case).
   */
  sectionBase?: string;
  /**
   * Demote-don't-delete at view grain. A hidden view stays registered →
   * `getDomainView` still resolves it (old deep links, coverage, mount
   * lockstep) but is omitted from in-page tabs. ModePage hops a hidden
   * `?sub=` onto the domain's first visible view rather than mounting the
   * demoted body.
   */
  hidden?: boolean;
}

export interface Domain {
  /** Owning Layer-1 mode (must be a real ModeSlug). */
  mode: ModeSlug;
  /** `?area=` value — unique within a mode. Usually the existing section/product slug. */
  slug: string;
  /** Sub-nav label. */
  label: string;
  /** One-line masthead description — what this section is FOR, in the house
   *  voice (short, plain, not press-release). Rendered under the section title by
   *  the shared `<Masthead>` so a mode section reads like every other page
   *  (docs/design/PAGE-HEADER-UNIFICATION-PRD.md). Optional only so the registry
   *  can be filled incrementally; every live domain should carry one. */
  description?: string;
  /** Icon key (string, resolved to JSX in the mount map — keeps the registry data-only). */
  icon: string;
  /** Commitment kinds this domain's objects map to (the schema slice). */
  objectKinds: string[];
  /** The domain's views, in sub-nav order. The first is the home view. */
  views: DomainView[];
  /** Canonical landing route under the modes nav: `/<mode>?area=<slug>`. */
  homeRoute: string;
  tier: DomainTier;
  /** Live | Orphaned | Rebuild (artifact 14 status) — read by the coverage gate. */
  status: "live" | "orphaned" | "rebuild";
  /**
   * Demote-don't-delete (Customer Module §5.1): a hidden domain stays
   * registered → resolvable (getDomain) → coverage-counted → redirect-target,
   * but is NOT listed in the per-mode sub-nav (domainsForMode filters it). The
   * Customer Module folds sales/marketing/market/support this way so the
   * Portfolio is the single front door, with zero 404s for old deep links.
   */
  hidden?: boolean;
}

/** `/<mode>?area=<slug>` (workspace tier) — the domain's canonical landing. */
export function domainHomeRoute(d: Pick<Domain, "mode" | "slug">): string {
  return `/${d.mode}?area=${d.slug}`;
}

/**
 * The per-mode sub-nav href for a domain's home. A ROUTE-owning domain (its home
 * view sets `route` — e.g. Docs→`/product/docs`, Lens→`/agents/lens`) links
 * STRAIGHT to that route so the nav tab doesn't bounce through the home (a wasted
 * hop + a flash), and stays workspace-global regardless of scope.
 *
 * A component-mounted domain now links to its canonical NAME-MATCHING PATH
 * (`/work/issues`, not `?area=queue`) — the Issue-3 "flip now" decision (Matt,
 * 2026-07-15): the path is the canonical URL, `?area=` redirects to it. Scoped to
 * the active project when one is set, so the sub-nav tabs stay inside the project
 * the left rail already scoped to. Pure. */
export function domainNavHref(d: Domain, projectSlug?: string | null): string {
  const route = d.views[0]?.route;
  if (route) return route;
  // Issue 3 flip: work/product/operations link to canonical section PATHS (they own
  // the `[...segments]` routes + the `?area=`→path redirect). Customers joined them
  // in Issue 1a (its lifecycle sections are real rail children now, not just cuts).
  // (Agents/personal are route-owning and returned above, so they never reach here.)
  if (PATH_ROUTED_MODES.has(d.mode)) return domainSectionPath(d, projectSlug);
  return projectSlug
    ? `/projects/${projectSlug}/${d.mode}?area=${d.slug}`
    : domainHomeRoute(d);
}

/** Modes whose sections are addressable as real PATHS (Issue 3 / Issue 1a flips) —
 *  they own a `[...segments]` route + the `?area=`→path redirect. Others keep
 *  `?area=` (agents/personal are route-owning; personal has no sections). */
export const PATH_ROUTED_MODES: ReadonlySet<ModeSlug> = new Set([
  "work",
  "product",
  "operations",
  "customers",
]);

// ── The registry ──────────────────────────────────────────────────────────────
// P0 seeds the domains with a REAL binding (orphaned `component` mounts +
// route-owning Agents). Rebuild views (Customers ▸ Contacts/Forecasting) and the
// remaining T2/T3 domains land in later phases (§6.6/§6.7); the five-mode
// invariant means each phase only ADDS registry rows, never a mode.

export const DOMAINS: readonly Domain[] = [
  // ── WORK (owner = me) ──────────────────────────────────────────────────────
  // Work slices by OWNER, not a section, so each domain is its own slug keyed
  // directly in CORE_ROUTE (the §5.1 principled exception).
  // Issues — the merged attention domain (Work-mode consolidation, PR-1;
  // docs/ia-audit/WORK-MODE-CONSOLIDATION-AUDIT.md §2). The former standalone
  // Queue · Reviews · Inbox tabs collapse into ONE domain with routed `?sub`
  // views (Tasks · Board · Reviews · Inbox), navigated by the in-page IssuesSubNav
  // (a tested NavTabs strip). Slug stays `queue` (every redirect/CORE_ROUTE/pulse
  // group keys on it); only the label + views change. Renamed "Issues" (the name
  // the app already uses everywhere — /issues, the metadata title, the "All
  // issues" pill) so the nav reads in the user's words, not the internal "Queue".
  {
    mode: "work",
    slug: "queue",
    label: "Issues",
    description: "Every task and where it stands — board, planner, reviews, and inbox.",
    icon: "boxes",
    objectKinds: ["task"],
    tier: "T1",
    status: "orphaned",
    homeRoute: "/work?area=queue",
    views: [
      // Tasks — the home: the all-tasks/all-statuses ledger (list + segmented
      // status filter). The bare `?area=queue` lands here (views[0]).
      { slug: "tasks", label: "Tasks", kind: "board", component: "work.queue" },
      // Board — the latent Kanban, restored via defaultQuery (the mount strips
      // `?view`, so the DomainView's default layers it back — the sanctioned seam;
      // NOT the TasksViewMenu, which writes a stripped param and is a dead control).
      { slug: "board", label: "Board", kind: "board", component: "work.queue", defaultQuery: { view: "board" } },
      // Planner — the month calendar over the SAME task rows as Tasks/Board (it
      // moves `due_date` where the board moves `status`), so it folds under Issues
      // as a `?sub` view, not under Plan/intent (Work-mode consolidation, PR-2 §3).
      { slug: "planner", label: "Planner", kind: "dashboard", component: "work.planner" },
      // Reviews — domain-flip to the canonical review door (3.B). The Work
      // splat `/work/issues/reviews` and `?area=queue&sub=reviews` follow this
      // route; they are not a second inbox.
      { slug: "reviews", label: "Reviews", kind: "list", route: "/agents/verification" },
      // Inbox — the per-user lens (my tasks + @mentions + read/unread watermark).
      { slug: "inbox", label: "Inbox", kind: "list", component: "work.inbox" },
    ],
  },
  // Goals (alias) — a hidden route-owning alias for the visible Goals domain below
  // (whose internal slug is `objectives`). Work▸Goals was a byte-identical dup of
  // /agents/goals; the canonical Work goal surface is the `objectives`-slug domain
  // below, renamed "Plan" → "Goals" (2026-07-16). This row survives so stale
  // `/work?area=goals` deep links + goal-kind Play dive-ins resolve into it (→
  // `?area=objectives`); the hidden-alias guard fires its route before the Lane
  // overlay. (/agents/goals is untouched — still the byte-identical GoalsView for
  // the agents-mode goal board.)
  {
    mode: "work",
    slug: "goals",
    label: "Goals",
    description: "The goals you're driving toward, and the work rolling up to them.",
    icon: "target",
    objectKinds: ["goal"],
    tier: "T1",
    status: "live", // alias-only (redirects into the Goals domain); no legacy round-trip of its own
    hidden: true,
    homeRoute: "/work?area=goals",
    views: [{ slug: "goals", label: "Goals", kind: "list", route: "/work?area=objectives" }],
  },
  // Goals — the workspace goal surface: StrategicGoalsSurface on top (the strategic
  // layer, the former Objectives — a metric with a committed target) with the
  // agentic GoalsView ledger beneath (the goals that roll up to those objectives).
  // Renamed "Plan" → "Goals" (Matt, 2026-07-16): one concept, one name everywhere —
  // rail tab, breadcrumb, page title, and Pulse band all read "Goals" (Issue 1b).
  // The URL follows the LABEL (`sectionPathSlug`), so the canonical path is
  // `/work/goals`; the internal `slug` STAYS `objectives` (every static link + the
  // /agents/objectives redirect keeps working), and the hidden `goals` alias above
  // keeps `?area=goals` deep links + goal-kind Play dive-ins resolving here.
  {
    mode: "work",
    slug: "objectives",
    label: "Goals",
    description: "The goals you're driving toward, and the work rolling up to them.",
    icon: "target",
    objectKinds: ["goal"],
    tier: "T1",
    status: "live",
    homeRoute: "/work?area=objectives",
    views: [{ slug: "objectives", label: "Goals", kind: "list", component: "work.objectives" }],
  },
  // Projects — the issue-centric projects surface (Matt, 2026-07-16: "roll issues
  // into projects … create new projects and have tasks automatically roll into
  // them"). Projects are ALREADY first-class (every task has project_id NOT NULL);
  // this is the work-organizing lens over them: each project shown with its live
  // issues, "+ New project", and drag/menu to re-home an issue between projects
  // (the moveTaskToProject / task_update({project}) capability). Made the CANONICAL
  // projects home (Matt's pick): `/projects` redirects here; the scoped
  // `/projects/<slug>/…` tier + Product ▸ Projects relationship graph stay.
  {
    mode: "work",
    slug: "projects",
    label: "Projects",
    description: "Your projects and the issues rolling into each one.",
    icon: "folderKanban",
    objectKinds: ["task"],
    tier: "T1",
    status: "live",
    homeRoute: "/work?area=projects",
    views: [{ slug: "projects", label: "Projects", kind: "board", component: "work.projects" }],
  },
  // Chat (Rooms R1+R2, goal #62) — durable team rooms (channels / DMs / program
  // rooms). A peer Work domain, NOT the dock copilot surface: rooms are
  // many-to-many actor-authored chat; the dock stays the single assistant
  // surface (docs/planning/FOUNDRY-PRD.md §8-Q1, docs/chat/ROOMS-V2-BUZZ-PRD.md).
  // Path is `/work/chat` (label-slug) and the legacy `?area=chat` form both
  // resolve here; search hits deep-link `/work/chat?room=<slug>`.
  {
    mode: "work",
    slug: "chat",
    label: "Chat",
    description: "Durable team rooms — program updates, channels, and agent posts.",
    icon: "messagesSquare",
    objectKinds: [],
    tier: "T1",
    status: "live",
    homeRoute: "/work/chat",
    views: [{ slug: "chat", label: "Chat", kind: "custom", component: "work.chat" }],
  },
  // Roadmap — FOLDED into Work ▸ Goals (Phase 2.2 / M6). Initiatives ARE
  // agentic goals; one place they live is `/work/goals`. This Work row survives
  // ONLY as a HIDDEN route-owning alias so stale `/work/roadmap` + `?area=roadmap`
  // deep links land on Goals instead of 404ing — the same demote-don't-delete
  // shape as Planner/Inbox. Work's visible rail is Issues · Goals · Projects ·
  // Chat. objectKinds keeps ["goal"] so a goal-kind dive-in that resolved here
  // still resolves (domainForKind doesn't filter hidden); the mode-page
  // hidden-alias guard fires its route before the Lane overlay.
  {
    mode: "work",
    slug: "roadmap",
    label: "Roadmap",
    description: "What's planned and in flight, by release.",
    icon: "milestone",
    objectKinds: ["goal"],
    tier: "T2",
    status: "live", // alias-only (redirects into Work ▸ Goals); no legacy round-trip of its own
    hidden: true,
    homeRoute: "/work?area=roadmap",
    views: [{ slug: "roadmap", label: "Roadmap", kind: "dashboard", route: "/work/goals" }],
  },
  // Planner — RE-FOLDED into Issues (2026-07-16, Matt: "planner is just a view of
  // the issues on a schedule … it doesn't have to be there"). Planner is the month
  // calendar over the SAME task rows as Issues (it schedules `due_date`), so it is a
  // VIEW of Issues, not a peer concept — reachable as the Issues `?sub=planner` /
  // `/work/issues/planner` Calendar tab. Demoted back to a hidden route-owning alias
  // (demote-don't-delete): `/work/planner` (+ the `/planner` legacy redirect) resolves
  // into the Issues Calendar view. Its Pulse touchpoints (On-time / Scheduling
  // coverage) folded into the Issues (queue) band; the standalone Planner band + lane
  // are retired (the same re-fold Inbox/Reviews took).
  {
    mode: "work",
    slug: "planner",
    label: "Planner",
    description: "Your tasks on a calendar — what's due, and when.",
    icon: "map",
    objectKinds: ["task"],
    tier: "T2",
    status: "live",
    hidden: true,
    homeRoute: "/work?area=planner",
    views: [{ slug: "planner", label: "Planner", kind: "dashboard", route: "/work?area=queue&sub=planner" }],
  },
  // Inbox — RE-FOLDED into Issues (2026-07-16 re-consolidation, Matt: "Issues now
  // contains inbox and reviews … take out the bars and their left navs"). Demoted
  // back to a hidden route-owning alias: `/work/inbox` resolves into the Issues
  // `?sub=inbox` tab — the SAME WorkInboxView the Issues surface already mounts. Its
  // Pulse touchpoints (Unread/Mentions) folded into the Issues (queue) band.
  // demote-don't-delete so stale deep links keep resolving. (NB: `/notifications`
  // no longer redirects here — it is now the standalone Notification Center page.)
  {
    mode: "work",
    slug: "inbox",
    label: "Inbox",
    description: "What's landed for you — assignments, mentions, and unread updates.",
    icon: "activity",
    objectKinds: [],
    tier: "T2",
    status: "live",
    hidden: true,
    homeRoute: "/work?area=inbox",
    views: [{ slug: "inbox", label: "Inbox", kind: "list", route: "/work?area=queue&sub=inbox" }],
  },
  // Reviews — hidden route-owning alias. 3.B: the canonical review door is
  // `/agents/verification` (C3). `/work/reviews`, `/work?area=reviews`, and the
  // Issues reviews view all flip there. objectKinds keeps ['task','action'] so
  // Play action-card dive-ins still resolve (domainForKind does not filter hidden).
  {
    mode: "work",
    slug: "reviews",
    label: "Reviews",
    description: "Work waiting on your sign-off before it ships.",
    icon: "shieldCheck",
    objectKinds: ["task", "action"],
    tier: "T1",
    status: "live",
    hidden: true,
    homeRoute: "/work?area=reviews",
    views: [{ slug: "review", label: "Reviews", kind: "list", route: "/agents/verification" }],
  },
  // Repos — DEMOTED to a hidden route-owning alias. The git-repo registry that
  // feeds the commit panes is agent-infrastructure config (which repos the fleet
  // ships against), not a daily Work destination, so its canonical home moved to
  // Agents ▸ Settings ▸ Repositories (`/settings/agents/repos`, beside Autopilot /
  // Mirror). It briefly lived as a visible Work-rail peer (STRANDED-REMEDIATION-PRD.md
  // W3) after being stranded in Settings; this hidden row survives ONLY so stale
  // `/work?area=repos` deep links redirect to the new settings home instead of
  // falling through to the Work dashboard. `/settings/repos` redirects there too.
  {
    mode: "work",
    slug: "repos",
    label: "Repos",
    description: "The git repositories your agents build and ship against.",
    icon: "gitBranch",
    objectKinds: [],
    tier: "T2",
    status: "live", // alias-only (redirects into Agents settings); no legacy round-trip of its own
    hidden: true,
    homeRoute: "/work?area=repos",
    views: [{ slug: "repos", label: "Repos", kind: "dashboard", route: "/settings/agents/repos" }],
  },

  // ── CUSTOMERS (lifecycle sections — hidden from the rail, Phase 2.2) ──
  // Marketing · Sales · Onboarding · Support · Renewal · Market stay registered
  // and resolvable (getDomain / deep links / /market) but `domainsForMode`
  // no longer lists them. Portfolio was already a hidden aggregate root.
  // The data-CUTS (List/Pipeline/Forecast/Properties/Queue) stay demoted in-body per
  // Issue 18 (see customers/page.tsx). Customers is now PATH_ROUTED (Issue 1a flip).

  // Marketing — Web Analytics (GA4/Plausible, T1) + the SEO workbench (T2). These
  // are PER-PROJECT surfaces (analytics sources / SEO targets are configured per
  // project), so they mount at the SCOPED tier and degrade to a project-picker at
  // the workspace tier (§5.5 — never a 404).
  {
    mode: "customers",
    slug: "marketing",
    label: "Marketing",
    description: "How people find you — web analytics and search.",
    icon: "telescope",
    objectKinds: [],
    tier: "T1",
    status: "orphaned",
    homeRoute: "/customers?area=marketing",
    hidden: true, // Phase 2.2: business-domain hide. Surface stays resolvable.
    views: [
      { slug: "analytics", label: "Web Analytics", kind: "dashboard", component: "marketing.analytics" },
      { slug: "seo", label: "SEO", kind: "dashboard", component: "marketing.seo" },
    ],
  },
  // Sales — the CRM pipeline (leads/deals). slug = the `sales` section; the per-app
  // surfaces are ?sub views (Contacts + Forecasting are REBUILD — §5.8a). Label is
  // "Sales" (Issue 1b — it matches the pulse band; the old domain label "Pipeline"
  // is now just the home view's label, not the section name).
  {
    mode: "customers",
    slug: "sales",
    label: "Sales",
    description: "Leads, opportunities, and the pipeline that turns them into revenue.",
    icon: "kanban",
    objectKinds: ["lead", "deal"],
    tier: "T1",
    status: "orphaned",
    homeRoute: "/customers?area=sales",
    hidden: true, // Phase 2.2: business-domain hide. Surface stays resolvable.
    views: [
      { slug: "pipeline", label: "Pipeline", kind: "board", component: "crm.pipeline" },
      { slug: "leads", label: "Leads", kind: "list", component: "crm.leads" },
      { slug: "accounts", label: "Accounts", kind: "list", component: "crm.accounts" }, // REBUILD — List over organizations (§3.6, cap line 1172)
      { slug: "contacts", label: "Contacts", kind: "list", component: "crm.contacts" }, // REBUILD (§5.8a)
      { slug: "forecast", label: "Forecasting", kind: "dashboard", component: "crm.forecast" }, // REBUILD (§5.8a / UI-UX §5.2.4)
      { slug: "opportunities", label: "Opportunities", kind: "list", component: "crm.opportunities" },
      { slug: "sequences", label: "Sequences", kind: "custom", component: "crm.sequences" },
    ],
  },
  // Onboarding — the new-customer lifecycle stage. A THIN section with no bespoke
  // mount, so its VISIBLE domain carries ZERO views and its landing is the seeded
  // Onboarding Lane's health (mode-page surfaces the Lane for a visible lifecycle-
  // section domain with no views — kickoff/data-import/integrations/go-live). status
  // "live" (net-new surface — no legacy route, so the round-trip check skips it).
  {
    mode: "customers",
    slug: "onboarding",
    label: "Onboarding",
    description: "Getting new customers live — kickoff, data import, and go-live.",
    icon: "compass",
    objectKinds: [],
    tier: "T2",
    status: "live",
    homeRoute: "/customers?area=onboarding",
    hidden: true, // Phase 2.2: business-domain hide. Surface stays resolvable.
    views: [],
  },
  // Support — cases ARE tasks (Nav D9, 2026-07-20): the view lists open
  // customer-success-section tasks across projects via the canonical
  // UnifiedIssuesList, with the CRM-derived health strip above when customers
  // exist. No cases table (support_tickets is tombstoned prose-side); a support
  // request rides the same queue/planner/reviews as every other task.
  // objectKinds stays ["lead"] — the customer PARTY is still the domain's noun;
  // there is no "case" commitment kind.
  {
    mode: "customers",
    slug: "support",
    label: "Support",
    description: "Open customer issues and the health of each account.",
    icon: "lifeBuoy",
    objectKinds: ["lead"],
    tier: "T2",
    status: "live",
    homeRoute: "/customers?area=support",
    hidden: true, // Phase 2.2: business-domain hide. Surface stays resolvable.
    views: [{ slug: "support", label: "Support", kind: "dashboard", component: "crm.support" }],
  },
  // Renewal — the renewal / recurring-revenue lifecycle stage. A REAL surface now
  // (the RETENTION book): the `crm.renewal` view derives the customer base's
  // recurring revenue + churn risk from the `customerParties` read model, so the
  // section is first-class content, not the empty seeded-Lane scaffold it used to
  // fall through to. Distinct from Sales on purpose — renewal is retention/expansion
  // (CS/AM), not new-logo acquisition. Label "Renewal" (Matt's word; Issue 1b).
  {
    mode: "customers",
    slug: "renewal",
    label: "Renewal",
    description: "Recurring revenue, renewals, and the accounts at risk of churning.",
    icon: "wallet",
    objectKinds: ["person", "organization"],
    tier: "T2",
    status: "live",
    homeRoute: "/customers?area=renewal",
    hidden: true, // Phase 2.2: business-domain hide. Surface stays resolvable.
    views: [{ slug: "renewal", label: "Renewal", kind: "dashboard", component: "crm.renewal" }],
  },

  // ── CUSTOMERS ▸ Portfolio (demoted alias) + Market (hidden, Phase 2.2) ──────
  // Portfolio stays a hidden alias (the aggregate root). Market was a visible
  // rail tab after Renewal (task 43502, D6); Phase 2.2 hides it with the other
  // customer business domains. /market now re-points to Work; direct
  // /customers?area=market still mounts this hub.
  // Portfolio is the Customer Module home (docs/customer-module/PRD.md §3.2) — the
  // account-rolled-up customer base over person/organization parties. It is the
  // mode's AGGREGATE root: bare `/customers` IS the Portfolio (threaded as the mode
  // `dashboardSlot`), so it is NOT a rail sibling — HIDDEN here; `?area=portfolio`
  // folds to `/customers` (mode-page). Kept resolvable so old deep links never 404.
  {
    mode: "customers",
    slug: "portfolio",
    label: "Portfolio",
    description: "Your whole customer base, rolled up by account.",
    icon: "users",
    objectKinds: ["person", "organization"],
    tier: "T1",
    status: "live",
    hidden: true, // Issue 1a: the aggregate root (bare /customers), not a rail section
    homeRoute: "/customers?area=portfolio",
    views: [{ slug: "list", label: "List", kind: "list", component: "crm.portfolio" }],
  },
  // Market Intelligence — a cross-cutting Customers domain (ICP · SWOT · competitors
  // · findings). Hidden from the Customers rail (Phase 2.2). The hub still mounts
  // at /customers?area=market; /market itself now folds into Work.
  // Wave 6 4.5: there is no CAPABILITY_SURFACES market manifest to tombstone
  // (C2 — this file stays at excellent/src/modes/domains.ts). Domain UI is
  // hidden, not write-dead: market.hub + /market redirect + MCP tools remain.
  {
    mode: "customers",
    slug: "market",
    label: "Market",
    description: "The competitive landscape — your ICP, rivals, and where you stand.",
    icon: "telescope",
    objectKinds: ["competitor", "swot", "icp"],
    tier: "T2",
    status: "orphaned",
    homeRoute: "/customers?area=market",
    hidden: true, // Phase 2.2: business-domain hide. /market re-pointed in Phase 2.3.
    views: [{ slug: "market", label: "Market", kind: "dashboard", component: "market.hub" }],
  },

  // ── PRODUCT (roadmap → build → ship) ───────────────────────────────────────
  // Projects = the SCOPED relationship view of one project: its product umbrella,
  // its child codebases, and its typed connections (the v59 project graph). The
  // workspace projects CATALOG (folders / archive / adopt) is a workspace-mgmt
  // surface with its own home — `/projects`, the "Organize projects" entry in the
  // project switcher — NOT a Product question, so at the workspace tier
  // `/product?area=projects` folds there (ModePage redirect). `live`: the scoped
  // view renders today and `/projects` resolves to null (no section legacy), so
  // the round-trip check skips it.
  //
  // HIDDEN from the Product left nav (UI-UX-PATTERNS-PRD Issue 16 — "settings/admin
  // surfaces don't belong in mode nav"). The catalog reads as SETTINGS, not delivery,
  // and the code already redirects `/product?area=projects` → `/projects` at the
  // workspace tier. `hidden` keeps the row resolvable (getDomain / coverage-counted)
  // so the scoped relationship view + every deep link still work, but the Product
  // rail now lists only the real delivery sections (Roadmap · Delivery · Docs) —
  // the same demote-don't-delete shape as Repos → Agents ▸ Settings ▸ Repositories.
  {
    mode: "product",
    slug: "projects",
    label: "Projects",
    description: "How a project's product, codebases, and connections fit together.",
    icon: "boxes",
    objectKinds: ["project", "task"],
    tier: "T1",
    status: "live",
    hidden: true, // Issue 16: catalog is a settings surface, not a Product delivery tab
    homeRoute: "/product?area=projects",
    views: [{ slug: "projects", label: "Projects", kind: "board", component: "projects.links" }],
  },
  // Roadmap — folded into Work ▸ Goals (Phase 2.2 / M6). Hidden route-owning
  // alias: `/product/roadmap` and `?area=roadmap` redirect into `/work/goals`
  // so old deep links do not 404. Quality and Delivery ▸ performance stay visible.
  // CAPABILITY_SURFACES is untouched (re-aim, never delete).
  {
    mode: "product",
    slug: "roadmap",
    label: "Roadmap",
    description: "What's planned and in flight, by release.",
    icon: "milestone",
    objectKinds: ["goal"],
    tier: "T2",
    status: "rebuild",
    homeRoute: "/product?area=roadmap",
    hidden: true, // Phase 2.2 / M6: folded into Work ▸ Goals. Route stays resolvable.
    views: [{ slug: "roadmap", label: "Roadmap", kind: "dashboard", route: "/work/goals" }],
  },
  // Delivery — the development section's per-project surfaces (performance audit,
  // PRD viewer). Scoped-tier mounts; workspace tier picks a project.
  {
    mode: "product",
    slug: "development",
    label: "Delivery",
    description: "Releases, performance, and the specs behind what's shipping.",
    icon: "activity",
    objectKinds: ["release"],
    tier: "T2",
    status: "orphaned",
    homeRoute: "/product?area=development",
    views: [
      { slug: "performance", label: "Performance", kind: "dashboard", component: "delivery.performance" },
      { slug: "prd", label: "PRD", kind: "detail", component: "delivery.prd" },
    ],
  },
  // Quality — re-surfaces the EXISTING section-audit findings (severity-ranked),
  // filtered to the development + design section profiles, as a visible Product rail
  // tab AFTER Delivery (task #43502, D2; Matt-approved nav-IA change). Reuses the SAME
  // audit surface the Operations ▸ Audits domain mounts (SectionAuditPanel) — no fork,
  // no new data source — kept modest: the two function panels + a link into the full
  // /audits hub. status "live": net-new Product surface, so the round-trip check skips it.
  {
    mode: "product",
    slug: "quality",
    label: "Quality",
    description: "Where the code and design fall short — audit findings, ranked.",
    icon: "shieldCheck",
    objectKinds: [],
    tier: "T2",
    status: "live",
    homeRoute: "/product?area=quality",
    views: [{ slug: "quality", label: "Quality", kind: "dashboard", component: "product.quality" }],
  },
  // Docs (the documentation browser/editor/viewer) was EXTRACTED into the
  // standalone @excellent-so/selfdocs app (Apps runner, #43475) — the
  // `/product/docs/*` route-owning domain is gone; the synced document_* tables
  // remain (additive-only) under the slimmed document capability manifest.

  // ── OPERATIONS (the back office) ───────────────────────────────────────────
  // Finance = the runway/revenue ledger (orphaned finance surface, mounted via a
  // thin server adapter that mirrors app/finance/page.tsx).
  {
    mode: "operations",
    slug: "finance",
    label: "Finance",
    description: "Bank, invoices, runway — the money moving through the business.",
    icon: "wallet",
    objectKinds: ["transaction", "money_account"],
    tier: "T1",
    status: "orphaned",
    homeRoute: "/operations?area=finance",
    views: [
      { slug: "ledger", label: "Finance", kind: "dashboard", component: "finance.ledger" },
    ],
  },

  // Section Audits — the best-in-class agentic audit panel (Run-all +
  // Fix-with-agent), mounted from the workspace-global /audits hub.
  {
    mode: "operations",
    slug: "audits",
    label: "Audits",
    description: "Where each part of the business falls short of best-in-class.",
    icon: "shieldCheck",
    objectKinds: [],
    tier: "T2",
    status: "orphaned",
    homeRoute: "/operations?area=audits",
    views: [{ slug: "audits", label: "Section audits", kind: "dashboard", component: "ops.audits" }],
  },
  // Company — the workings of the firm itself (stream-registry.ts: "the firm
  // itself: obligations, decisions, position"). Redesigned 2026-07-20 (Matt:
  // "team and hiring should go there; the company page describes the workings of
  // the company itself"): the former Team + Hiring domains FOLD IN as views of
  // this one section, and the Overview is a live company dashboard, not a
  // stage-link board. The Decide stage's Organizational Simulation (MASTER-PRD
  // §6.5.7) gets its own Simulate view. The ATS views keep `sectionBase:
  // "hiring"` so their `${basePath}/…` links stay on the real
  // `/projects/sections/hiring/*` routes. status "live": net-new surface, the
  // round-trip check skips it.
  //
  // M5 mothball: Company stays resolvable so tenant-1 specimens and old
  // deep links keep working; it is no longer a rail tab. The verification
  // product keeps ONE visible view — Team (workspace members). Hiring ATS
  // views (pipeline / jobs / candidates / job board / simulate / settings)
  // and the old Overview stay registered but hidden so ModePage hops them
  // onto Team instead of mounting the demoted ATS.
  {
    mode: "operations",
    slug: "company",
    label: "Team",
    description: "People with access to this workspace.",
    icon: "building2",
    objectKinds: [],
    tier: "T2",
    status: "live",
    hidden: true, // M5: back-office mothball. Surface stays resolvable.
    homeRoute: "/operations?area=company",
    views: [
      { slug: "team", label: "Team", kind: "list", component: "team.directory" },
      { slug: "company", label: "Overview", kind: "dashboard", component: "company.board", hidden: true },
      { slug: "pipeline", label: "Hiring", kind: "board", component: "ats.pipelineHub", sectionBase: "hiring", hidden: true },
      { slug: "jobs", label: "Jobs", kind: "list", component: "ats.jobs", sectionBase: "hiring", hidden: true },
      { slug: "candidates", label: "Candidates", kind: "list", component: "ats.candidates", sectionBase: "hiring", hidden: true },
      // ?sub=jobs/<slug>/pipeline → the drag-stage board for one job.
      { slug: "jobs/pipeline", label: "Job board", kind: "board", component: "ats.jobPipeline", sectionBase: "hiring", hidden: true },
      // The Decide stage's simulation lab (the firm reasons about itself).
      { slug: "simulate", label: "Simulate", kind: "custom", component: "company.simulate", hidden: true },
      // The ATS config (pipelines, scorecards, sources, integrations).
      { slug: "settings", label: "Hiring settings", kind: "custom", component: "ats.settings", sectionBase: "hiring", hidden: true },
    ],
  },

  // Team (alias) — FOLDED into Company (2026-07-20). Hidden route-owning alias:
  // `?area=team` / `/operations/team` resolves into Company ▸ Team (the same
  // team.directory mount). objectKinds stay so person/org Play dive-ins resolve
  // here (`?area=team&sub=directory` — sub === the alias's own home slug, so the
  // alias redirect keeps its pinned `sub=team`).
  {
    mode: "operations",
    slug: "team",
    label: "Team",
    description: "The people who make up your company, and their roles.",
    icon: "users",
    objectKinds: ["person", "organization"],
    tier: "T2",
    status: "live", // alias-only (redirects into Company); no legacy round-trip of its own
    hidden: true,
    homeRoute: "/operations?area=team",
    views: [{ slug: "directory", label: "Team", kind: "list", route: "/operations?area=company&sub=team" }],
  },

  // ── OPERATIONS ▸ Hiring (alias) — the ATS folded into Company (2026-07-20;
  // it had folded from its own mode into Operations 2026-06-17). Hidden
  // route-owning alias: bare `?area=hiring` lands on Company ▸ Hiring (the
  // pipeline hub); a deep link's `?sub=` (jobs/candidates/settings/the job
  // drill) is carried through by the mode-page alias redirect, since hiring's
  // old view slugs are Company view slugs verbatim. objectKinds stay so
  // application/job/scorecard Play dive-ins resolve here (`&sub=pipeline`, the
  // alias's own home slug → the pinned sub wins). ──────────────────────────────
  {
    mode: "operations",
    slug: "hiring",
    label: "Hiring",
    description: "Open roles, candidates, and where each one is in the loop.",
    icon: "kanban",
    objectKinds: ["application", "job", "scorecard"],
    tier: "T1",
    status: "live", // alias-only (redirects into Company)
    hidden: true,
    homeRoute: "/operations?area=hiring",
    views: [
      { slug: "pipeline", label: "Hiring", kind: "board", route: "/operations?area=company&sub=pipeline" },
      { slug: "jobs", label: "Jobs", kind: "list", route: "/operations?area=company&sub=jobs" },
      { slug: "candidates", label: "Candidates", kind: "list", route: "/operations?area=company&sub=candidates" },
    ],
  },

  // ── AGENTS (autonomous systems — the proven Domain model, 0 LOST) ──────────
  // Every Agents domain is ROUTE-owning: its views set route:"/agents/<x>" and
  // ModePage resolves `/agents?area=<slug>` with a one-line redirect() to that
  // route (§3.5, §5.8c). The `/agents/*` subtree is EXEMPT from the modes
  // redirect, so these routes are loop-safe.
  {
    mode: "agents",
    slug: "overview",
    label: "Overview",
    description: "The state of your agent fleet at a glance.",
    icon: "layoutDashboard",
    objectKinds: [],
    tier: "T1",
    status: "live",
    homeRoute: "/agents?area=overview",
    views: [{ slug: "overview", label: "Overview", kind: "dashboard", route: "/agents" }],
  },
  // Goals — DEMOTED to a hidden route-owning alias (task #43502, D7): the shipped
  // Agents rail is Workflows · Analytics with Overview as the landing, so Goals is no
  // longer a rail tab (agent goals live in Work ▸ Goals). `/agents/goals` stays live
  // as a deep-link target; this row keeps `?area=goals` deep links + goal-kind Play
  // dive-ins resolving (getDomain/domainForKind don't filter hidden).
  {
    mode: "agents",
    slug: "goals",
    label: "Goals",
    description: "What your agents are working toward, and their progress.",
    icon: "target",
    objectKinds: ["goal"],
    tier: "T1",
    status: "live",
    hidden: true,
    homeRoute: "/agents?area=goals",
    views: [{ slug: "goals", label: "Goals", kind: "list", route: "/agents/goals" }],
  },
  // Runs — DEMOTED to a hidden route-owning alias (task #43502, D7; the run ledger
  // folded into Analytics ▸ Recent runs per #43500). `/agents/runs` is a redirect
  // stub (→ /agents/analytics#fleet-runs) that stays live; this row keeps stale
  // `?area=runs` deep links + run-kind Play dive-ins resolving.
  {
    mode: "agents",
    slug: "runs",
    label: "Runs",
    description: "Every agent run — what fired, when, and how it went.",
    icon: "activity",
    objectKinds: ["run"],
    tier: "T1",
    status: "live",
    hidden: true,
    homeRoute: "/agents?area=runs",
    views: [{ slug: "runs", label: "Runs", kind: "list", route: "/agents/runs" }],
  },
  // Sessions — SI-G2 (release plan XS-G10): the read-only live-session surface.
  // Route-owning like every Agents domain, so `/agents?area=sessions` deep-links
  // to the DataTable; the `/agents/sessions/$id` trajectory view is its detail.
  {
    mode: "agents",
    slug: "sessions",
    label: "Sessions",
    description: "Every live session and its trajectory, read-only.",
    icon: "radio",
    objectKinds: [],
    tier: "T1",
    status: "live",
    homeRoute: "/agents?area=sessions",
    views: [{ slug: "sessions", label: "Sessions", kind: "list", route: "/agents/sessions" }],
  },
  {
    mode: "agents",
    // The "You" sections of the unified Analytics surface (your personal Claude
    // Code usage). The area `slug`/`homeRoute` stay `lens` (identity — like a
    // section slug); `?area=lens` deep links keep resolving and bounce to the
    // `/agents/analytics/you` routes below. See
    // docs/agents/ANALYTICS-EFFICIENCY-INTEGRATION-PRD.md.
    slug: "lens",
    label: "Analytics",
    description: "Your agent usage — sessions, costs, activity, and tools.",
    icon: "telescope",
    objectKinds: ["run", "session"],
    tier: "T1",
    status: "live",
    homeRoute: "/agents?area=lens",
    // First view is the HOME (`?area=lens` + the Pulse "Analytics" band land here):
    // the unified Analytics Overview. The deeper views are the You sections.
    views: [
      { slug: "overview", label: "Overview", kind: "custom", route: "/agents/analytics" },
      { slug: "you", label: "You", kind: "custom", route: "/agents/analytics/you" },
      { slug: "sessions", label: "Sessions", kind: "custom", route: "/agents/analytics/you/sessions" },
      { slug: "costs", label: "Costs", kind: "custom", route: "/agents/analytics/you/costs" },
      { slug: "activity", label: "Activity", kind: "custom", route: "/agents/analytics/you/activity" },
      { slug: "tools", label: "Tools", kind: "custom", route: "/agents/analytics/you/tools" },
    ],
  },
  // Fleet — the VISIBLE "Workflows" rail tab (task #43502, D7). The slug stays `fleet`
  // (the pulse area key + the AGENTS_VIEWS `area:"fleet"` health-dot join both key on
  // it), but its home view presents as "Workflows" and route-owns /agents/workflows —
  // this is the domain backing the shipped rail's Workflows tab, so it STAYS visible.
  {
    mode: "agents",
    slug: "fleet",
    label: "Fleet",
    description: "The workflows your agents run, and how they're being tuned.",
    icon: "boxes",
    objectKinds: ["workflow", "variant"],
    tier: "T1",
    status: "live",
    homeRoute: "/agents?area=fleet",
    // Evaluations (Crucible: variants/evals/promotions) is a VIEW of Fleet under
    // /agents/workflows — there is NO /agents/evaluations route (§3.5).
    views: [
      { slug: "fleet", label: "Workflows", kind: "custom", route: "/agents/workflows" },
      { slug: "evaluations", label: "Evaluations", kind: "custom", route: "/agents/workflows" },
    ],
  },
  // Browser QA — real-browser tests of this app, driven by agents through the
  // browser_qa tool (docs/qa/AGENT-BROWSER-QA-PRD.md §5.9). Route-owning so the
  // shell renders the correct leaf title: ShellTitleBoundary makes a mounted
  // page's PageHeader defer, and without an entry here the shell would derive
  // "Qa" from the route tail. Registered alongside the rail tab in
  // app/agents/layout.tsx AGENTS_VIEWS — the two must stay in lockstep.
  {
    mode: "agents",
    slug: "qa",
    label: "Browser QA",
    description: "Real-browser test runs of this app, with the evidence each one produced.",
    icon: "bug",
    objectKinds: [],
    tier: "T2",
    status: "live",
    homeRoute: "/agents?area=qa",
    views: [{ slug: "qa", label: "Browser QA", kind: "custom", route: "/agents/qa" }],
  },
  // SKU A — Evidence & dual-agent door dogfood surface. Route-owning so the
  // shell titles the leaf "Verification" rather than deriving from the path.
  // #45273: UNHIDDEN — it now ships as a visible Agents rail tab (after
  // Verification ops) so the SKU A door has a nav entry instead of being an
  // operator-only deep link. Still not a new mode.
  {
    mode: "agents",
    slug: "verification",
    label: "Verification",
    description:
      "Evidence bundles, dual-agent gate, claims + falsifiers, receipts, silent-approve.",
    icon: "shield",
    objectKinds: [],
    tier: "T2",
    status: "live",
    homeRoute: "/agents?area=verification",
    views: [
      {
        slug: "verification",
        label: "Verification",
        kind: "custom",
        route: "/agents/verification",
      },
    ],
  },
  // Verification ops — §8 M1–M12 scorecard + holdout/admission/kill-switch/
  // export.emitted/open-weights tiles. Visible Agents rail tab (no new mode).
  {
    mode: "agents",
    slug: "verification-ops",
    label: "Verification ops",
    description:
      "§8 scorecard M1–M12, holdout ledger, admission, kill-switch drill, export audit, open-weights lock.",
    icon: "shieldCheck",
    objectKinds: [],
    tier: "T2",
    status: "live",
    homeRoute: "/agents?area=verification-ops",
    views: [
      {
        slug: "verification-ops",
        label: "Verification ops",
        kind: "dashboard",
        route: "/agents/verification-ops",
      },
    ],
  },
  // #46036 — 3.G claims/lanes operator view. Visible Agents rail tab.
  {
    mode: "agents",
    slug: "claims",
    label: "Claims",
    description: "Who holds what, for how long, and which lanes are alive.",
    icon: "gitBranch",
    objectKinds: [],
    tier: "T2",
    status: "live",
    homeRoute: "/agents?area=claims",
    views: [{ slug: "claims", label: "Claims", kind: "list", route: "/agents/claims" }],
  },
  // #46036 — 3.H triage board (wires #45530). Visible Agents rail tab.
  {
    mode: "agents",
    slug: "triage",
    label: "Triage",
    description: "The ranked board the agent queue already pulls from.",
    icon: "kanban",
    objectKinds: [],
    tier: "T2",
    status: "live",
    homeRoute: "/agents?area=triage",
    views: [{ slug: "triage", label: "Triage", kind: "list", route: "/agents/triage" }],
  },
  // Workflow roadmap — the fleet-internal roadmap: workflow templates blocked on
  // unauthored agent roles (page title literally "Workflow roadmap"). It is agent
  // plumbing reachable from the Workflows console, not a rail tab, so it is now a
  // HIDDEN route-owning alias (task #43502, D7) — the shipped rail is Workflows ·
  // Analytics. slug/area/route stay `roadmap` (`/agents/roadmap` deep links keep
  // resolving). (The product roadmap folded into Work ▸ Goals in M6.)
  {
    mode: "agents",
    slug: "roadmap",
    label: "Workflow roadmap",
    description: "Workflow templates waiting on agent roles to be authored.",
    icon: "map",
    objectKinds: [],
    tier: "T3",
    status: "live",
    hidden: true,
    homeRoute: "/agents?area=roadmap",
    views: [{ slug: "roadmap", label: "Workflow roadmap", kind: "custom", route: "/agents/roadmap" }],
  },

  // ── PERSONAL (owner = me; a personal knowledge corpus) ──────────────────────
  // Route-owning, like Agents: /personal serves its own board + search; no KPIs
  // ("no bars"). docs/personal/PRD.md. Hidden from the rail/dock while the UI
  // root folds into Work; the underlying routes stay resolvable.
  {
    mode: "personal",
    slug: "library",
    label: "Library",
    description: "Everything you've saved to read, reference, or come back to.",
    icon: "library",
    objectKinds: [],
    tier: "T1",
    status: "live",
    hidden: true,
    homeRoute: "/personal?area=library",
    views: [{ slug: "library", label: "Library", kind: "custom", route: "/personal" }],
  },
  {
    mode: "personal",
    slug: "collections",
    label: "Collections",
    description: "Your saved items, grouped into collections.",
    icon: "boxes",
    objectKinds: [],
    tier: "T1",
    status: "live",
    hidden: true,
    homeRoute: "/personal?area=collections",
    views: [{ slug: "collections", label: "Collections", kind: "custom", route: "/personal/collections" }],
  },
];

const BY_KEY: ReadonlyMap<string, Domain> = new Map(
  DOMAINS.map((d) => [`${d.mode}/${d.slug}`, d]),
);

// ── Custom-domain data tier (Data Design PRD §6.4) ──────────────────────────
// A user/agent-authored domain (`custom_domains` row, v113) is folded into the
// SAME registry the code domains ride, so it appears in its mode's sub-nav and
// resolves through the ONE parametric /<mode>?area=<slug> shell — no seventh
// mode, no new route file (§11: apps are DATA). This module stays PURE (LOCKED
// #4 — no DB import, no React): the DB read happens in `mode-page.tsx` (an async
// server component) which passes the rows in via the `custom` param below. The
// merge is soft-nav safe — it's a per-request server-composed list, and active
// state derives from `usePathname` in ModeRail exactly as it does for code
// domains, so a frozen layout under soft nav still lights the right tab.

/** The minimal shape `mode-page.tsx` passes in from `core.listCustomDomains`
 *  (structural — keeps this module free of a `@excellent/core` import, so it
 *  stays data-only/mirror-safe). */
export interface CustomDomainInput {
  slug: string;
  name: string;
  icon: string | null;
  mode: string;
  member_kinds: string[];
  home_view_uid: string | null;
  sort_order: number | null;
  enabled: boolean;
}

/** The component key a custom domain's home view carries — a single parametric
 *  mount bound in domain-mounts.tsx for every mode, which renders every custom
 *  domain from its pinned saved views + kind quick-links. `check-domain-mounts`
 *  requires it be declared as a string literal in the adapter below AND bound. */
export const CUSTOM_DOMAIN_COMPONENT = "custom.domain";

/** Pure adapter: a `custom_domains` row → the registry `Domain` shape. A custom
 *  domain is a component-mounted domain (its home is `?area=<slug>`, rendered by
 *  the parametric mount); it is never route-owning. */
export function customDomainToDomain(cd: CustomDomainInput): Domain {
  const mode = cd.mode as ModeSlug;
  return {
    mode,
    slug: cd.slug,
    label: cd.name,
    icon: cd.icon ?? "boxes",
    objectKinds: cd.member_kinds,
    tier: "T2",
    status: "live",
    homeRoute: `/${mode}?area=${cd.slug}`,
    views: [{ slug: cd.slug, label: cd.name, kind: "dashboard", component: "custom.domain" }],
  };
}

/** The one resolver ModePage calls: domain by (mode, ?area). A CODE domain
 *  always wins on a slug collision (the locked "real apps win" rule) — custom
 *  rows are consulted only when no code domain matches. `custom` is the
 *  per-request enabled custom-domain list (empty/omitted at build/pure sites). */
export function getDomain(
  mode: ModeSlug,
  area: string | undefined,
  custom?: readonly CustomDomainInput[],
): Domain | null {
  if (!area) return null;
  const code = BY_KEY.get(`${mode}/${area}`);
  if (code) return code;
  const cd = custom?.find((c) => c.enabled && c.mode === mode && c.slug === area);
  return cd ? customDomainToDomain(cd) : null;
}

/** True when a resolved Domain is a custom (data-authored) domain — ModePage
 *  branches on this to render the parametric CustomDomainView instead of a code
 *  mount. Keyed on the sentinel component so it's a pure check. */
export function isCustomDomain(domain: Domain): boolean {
  return domain.views[0]?.component === CUSTOM_DOMAIN_COMPONENT;
}

/** Views listed in in-page tabs. Hidden views stay resolvable via getDomainView. */
export function visibleDomainViews(domain: Domain): DomainView[] {
  return domain.views.filter((v) => !v.hidden);
}

/** First visible view, or views[0] if every view is hidden. */
export function domainHomeView(domain: Domain): DomainView {
  return visibleDomainViews(domain)[0] ?? domain.views[0]!;
}

/**
 * A view within a domain by (?sub). No sub → the domain's first visible (home)
 * view. A sub that matches (exact, or the head/tail shape for a path-shaped
 * slug like "jobs/pipeline" — the §5.7 legacy-leaf convention) → that view,
 * including hidden views so old deep links still resolve. A sub that is
 * PROVIDED but matches NOTHING → `null`, so the caller degrades to the generic
 * Objects view rather than silently rendering an unrelated home view (a user who
 * asked for `?sub=accounts` must not get the Pipeline board).
 */
export function getDomainView(domain: Domain, sub: string | undefined): DomainView | null {
  if (!sub) return domainHomeView(domain);
  const hit = domain.views.find((v) => v.slug === sub);
  if (hit) return hit;
  const shaped = domain.views.find((v) => v.slug.includes("/") && subMatchesShape(sub, v.slug));
  return shaped ?? null;
}

/** `jobs/acme/pipeline` matches the view slug `jobs/pipeline` (head + tail). */
function subMatchesShape(sub: string, viewSlug: string): boolean {
  const want = viewSlug.split("/");
  const got = sub.split("/");
  if (want.length < 2 || got.length < want.length) return false;
  return got[0] === want[0] && got[got.length - 1] === want[want.length - 1];
}

/** All VISIBLE domains of a mode, in registry order — the per-mode sub-nav
 *  source (§5.4). Hidden domains (Customer Module §5.1 fold) are excluded here
 *  but remain resolvable via getDomain (no 404, coverage-counted).
 *
 *  Data Design §6.4: ENABLED custom domains for the mode are appended after the
 *  code domains (registry merge), so an app authored as data appears in the
 *  sub-nav immediately. A custom row whose slug collides with a code domain in
 *  the same mode is DROPPED here (the code domain already occupies the slot and
 *  always wins on resolve — a shadow would be a dead nav entry). Disabled rows
 *  never appear. `custom` is empty/omitted at pure call sites. */
export function domainsForMode(mode: ModeSlug, custom?: readonly CustomDomainInput[]): Domain[] {
  const code = DOMAINS.filter((d) => d.mode === mode && !d.hidden);
  if (!custom?.length) return code;
  const taken = new Set(DOMAINS.filter((d) => d.mode === mode).map((d) => d.slug));
  const extra = custom
    .filter((c) => c.enabled && c.mode === mode && !taken.has(c.slug))
    .map(customDomainToDomain);
  return [...code, ...extra];
}

/** Find the domain that surfaces a given commitment kind (Play's Dive-in resolver,
 *  §2.5). A kind can appear in several domains (e.g. `task` ∈ Work▸Queue,
 *  Work▸Planner, Product▸Projects) — prefer the domain in the kind's canonical
 *  home mode (`modeForKind`) so the dive is deterministic, not first-match. */
export function domainForKind(kind: string): Domain | null {
  const candidates = DOMAINS.filter((d) => d.objectKinds.includes(kind));
  if (candidates.length <= 1) return candidates[0] ?? null;
  const home = modeForKind(kind);
  if (home) {
    const inHome = candidates.find((d) => d.mode === home);
    if (inHome) return inHome;
  }
  return candidates[0]!;
}

/**
 * The basePath an orphaned section-hosted page expects, derived from scope (§5.5).
 * The section routes stay live as deep-link targets, so a page's own
 * `${basePath}/jobs/…` links resolve correctly.
 */
export function scopeBasePath(domain: Pick<Domain, "slug">, projectSlug: string | null): string {
  const sectionSlug = domain.slug;
  return projectSlug
    ? `/projects/${projectSlug}/${sectionSlug}`
    : `/projects/sections/${sectionSlug}`;
}

export interface DomainViewProps {
  basePath: string;
  slug?: string;
  searchParams: Record<string, string | string[] | undefined>;
  /** The active project slug (null at the workspace tier). Section-scoped mount
   *  adapters (marketing analytics/SEO, delivery performance/PRD) need it to
   *  synthesize the `{ projectSlug, sectionSlug }` params their page expects, and
   *  to degrade gracefully to a project-picker when null. */
  projectSlug: string | null;
  /** The domain's section slug (= domain.slug) — the `sectionSlug` those pages guard on. */
  sectionSlug: string;
}

/** Build the props an orphaned/rebuild view component receives (§5.5 — a thin
 *  adapter). The live request `searchParams` are merged UNDER the view's
 *  `defaultQuery` (so a view's pinned default wins, but the page still sees the
 *  user's filter/search/page state — §5.5 contract rule 2). */
export function buildDomainViewProps(
  domain: Domain,
  dv: DomainView,
  projectSlug: string | null,
  sub: string | undefined,
  liveSearchParams?: Record<string, string | string[] | undefined>,
): DomainViewProps {
  // A view mounting another section's tree (`sectionBase`, e.g. Company mounting
  // the hiring ATS) keeps THAT section's basePath + sectionSlug, so its
  // `${basePath}/…` links and section guards stay on the real routes.
  const sectionSlug = dv.sectionBase ?? domain.slug;
  const basePath = scopeBasePath({ slug: sectionSlug }, projectSlug);
  // `jobs/<slug>/pipeline` → slug = the middle segment (the job slug).
  const parts = sub?.split("/") ?? [];
  const slug = parts.length >= 2 && parts[0] === "jobs" ? parts[1] : undefined;
  return {
    basePath,
    slug,
    // Drop the modes-routing params from what the mounted page sees (they are the
    // resolver's concern, not the page's), then layer the domain's defaults on top.
    searchParams: { ...stripModeParams(liveSearchParams), ...dv.defaultQuery },
    projectSlug,
    sectionSlug,
  };
}

const MODE_ROUTING_PARAMS = new Set(["area", "sub", "view"]);
function stripModeParams(
  sp: Record<string, string | string[] | undefined> | undefined,
): Record<string, string | string[] | undefined> {
  if (!sp) return {};
  const out: Record<string, string | string[] | undefined> = {};
  for (const [k, v] of Object.entries(sp)) if (!MODE_ROUTING_PARAMS.has(k)) out[k] = v;
  return out;
}

// ── Real section PATHS (UI-UX-PATTERNS-PRD Issue 3) ─────────────────────────────
// Sections are addressable as REAL paths (`/work/issues`, `/product/roadmap`), not
// only `?area=` query state — deep-linkable, shareable, and NAME-MATCHING (the path
// segment reads as the canonical display name, tying to Issue 1b). This is ADDITIVE:
// the `?area=` form still resolves (the Self-Nav bar navigates by it), and these
// helpers give the app-tree a path→Domain resolver for the `[...segments]` routes.
// The final "nav links to the path + `?area=` redirects to it" flip waits on the
// Self-Nav navigation-model decision, so it is deliberately NOT done here.

/** A Domain's canonical URL path segment — the display label slugified
 *  (`"Plan"` → `plan`, `"Web Analytics"` → `web-analytics`). The NAME is the URL
 *  (Issue 1b/3); the internal `slug` (`objectives`) stays the keying concern. */
export function sectionPathSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/** Resolve a real section-path segment (`/work/issues`) to its Domain. Matches the
 *  canonical display-name slug first (`issues` → the "Issues" domain, internal slug
 *  `queue`), then the raw area slug (`queue`) so a legacy slug-path still resolves.
 *  VISIBLE domains only — a hidden alias is reached via its own redirect, never a
 *  nav path. Pure; the `[...segments]` route uses it, `domains.ts` stays data-only. */
export function domainForPathSegment(mode: ModeSlug, seg: string): Domain | null {
  const s = seg.toLowerCase();
  const visible = DOMAINS.filter((d) => d.mode === mode && !d.hidden);
  return (
    visible.find((d) => sectionPathSlug(d.label) === s) ??
    visible.find((d) => d.slug === s) ??
    null
  );
}

/** The canonical section PATH for a Domain (`/work/issues`), the Issue-3 sibling of
 *  `domainHomeRoute` (`/work?area=queue`). Name-matching + project-scope aware. */
export function domainSectionPath(d: Pick<Domain, "mode" | "label">, projectSlug?: string | null): string {
  const seg = sectionPathSlug(d.label);
  return projectSlug ? `/projects/${projectSlug}/${d.mode}/${seg}` : `/${d.mode}/${seg}`;
}

/**
 * Map an `?area=<slug>` (+ optional `?sub=`) to the CANONICAL section PATH — the
 * Issue-3 "flip now" resolver (Matt, 2026-07-15). A VISIBLE domain uses its
 * name-matching segment (`queue` → `/work/issues`); a Lane or an as-yet-unknown
 * slug (the lifecycle bands: `build`, `ship`, …) keeps its slug as the segment
 * (`/product/build`), since the Lane slug IS the segment. A view slug becomes a
 * second path segment. Hidden aliases are deliberately NOT rewritten here — they
 * still round-trip through `ModePage`'s alias redirect to their real target, which
 * then flips to a path on the next hop. Pure; used by the mode `page.tsx` redirect
 * (the `?area=` → path safety net) and every path-emitting nav builder. */
export function areaToPath(
  mode: ModeSlug,
  area: string,
  sub?: string,
  projectSlug?: string | null,
): string {
  let d = getDomain(mode, area);
  // A HIDDEN alias forwards to its real target (its home view's `route`, a
  // `?area=<other>&sub=<view>` link). Follow it so the path is the CANONICAL
  // destination, not the alias slug: the Plan band's key `goals` → the objectives
  // domain → `/work/plan`; the Inbox/Reviews aliases → `/work/issues/<view>`
  // (they are folded views of Issues; Planner is a visible peer, not folded). A
  // route that isn't `?area=` (e.g. Repos →
  // `/settings/agents/repos`) is left to ModePage's own alias redirect. Planner is a
  // hidden alias too now (re-folded): its key follows to `/work/issues/planner`.
  if (d?.hidden) {
    const route = d.views[0]?.route ?? "";
    const q = route.indexOf("?");
    if (q >= 0) {
      const params = new URLSearchParams(route.slice(q + 1));
      const a = params.get("area");
      if (a) {
        area = a;
        sub = sub ?? params.get("sub") ?? undefined;
        d = getDomain(mode, area);
      }
    }
  }
  const seg = d && !d.hidden ? sectionPathSlug(d.label) : area;
  const base = projectSlug ? `/projects/${projectSlug}/${mode}/${seg}` : `/${mode}/${seg}`;
  if (!sub) return base;
  const view = d ? getDomainView(d, sub) : null;
  return `${base}/${view?.slug ?? sub}`;
}
